import { forwardRef } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Theme } from "@mui/material/styles";

import { AutocompleteOptionProps } from "../types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox/DashboardCheckbox";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronRight } from "@/icons/outlined";

export const AutocompleteOption = forwardRef<HTMLLIElement, AutocompleteOptionProps>(function AutocompleteOption(
  {
    label,
    secondaryLabel,
    icon,
    checkbox = false,
    checked = false,
    chevronRight = false,
    action,
    size = "standard",
    selected = false,
    disabled = false,
    sx,
    "data-test": dataTest,
    ...rest
  },
  ref
) {
  const isStandard = size === "standard";

  return (
    <Stack
      component="li"
      ref={ref}
      direction="row"
      alignItems="center"
      data-test={dataTest}
      sx={mergeSx(
        (theme: Theme) => ({
          padding: theme.spacing(1, 2),
          cursor: "pointer",
          opacity: disabled ? 0.4 : 1,
          pointerEvents: disabled ? "none" : "auto",
          backgroundColor: selected ? theme.palette.surface.brand.quiet : "transparent",
          "&:hover": {
            backgroundColor: theme.palette.surface.brand.quiet,
          },
        }),
        sx
      )}
      {...rest}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexShrink: 1 }}>
          {checkbox && (
            <DashboardCheckbox
              checked={checked}
              size={isStandard ? "medium" : "small"}
              sx={{ padding: 0 }}
              tabIndex={-1}
            />
          )}

          {icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                width: isStandard ? 20 : 16,
                height: isStandard ? 20 : 16,
              }}
            >
              {icon}
            </Box>
          )}

          <DashboardTypography
            variant={isStandard ? "body1" : "body2"}
            sx={(theme: Theme) => ({
              color: theme.palette.text.neutral.moderate,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            })}
          >
            {label}
          </DashboardTypography>
        </Stack>

        {secondaryLabel && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0, flexShrink: 100, overflow: "hidden" }}
          >
            <Box
              sx={(theme: Theme) => ({
                width: 4,
                height: 4,
                borderRadius: "50%",
                backgroundColor: theme.palette.text.brand.quiet,
                flexShrink: 0,
              })}
            />
            <DashboardTypography
              variant={isStandard ? "body2" : "caption"}
              sx={(theme: Theme) => ({
                color: theme.palette.text.brand.quiet,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              })}
            >
              {secondaryLabel}
            </DashboardTypography>
          </Stack>
        )}
      </Stack>

      {action && <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{action}</Box>}

      {chevronRight && (
        <DashboardIcon size="small" sx={(theme: Theme) => ({ color: theme.palette.text.neutral.moderate })}>
          <ChevronRight />
        </DashboardIcon>
      )}
    </Stack>
  );
});

AutocompleteOption.displayName = "AutocompleteOption";
